import React from 'react';
import Plot from 'react-plotly.js';
import { Box, useTheme } from '@mui/material';
import { TimeSeriesPoint } from '../../../types/analytics.ts';

interface TimeSeriesChartProps {
  data: TimeSeriesPoint[];
  forecast?: TimeSeriesPoint[];
  confidenceIntervals?: {
    upper: number[];
    lower: number[];
  };
  title: string;
  height?: number;
}

const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({
  data,
  forecast,
  confidenceIntervals,
  title,
  height = 400,
}) => {
  const theme = useTheme();

  // Prepare historical data
  const historicalDates = data.map(point => point.timestamp);
  const historicalValues = data.map(point => point.value);

  // Prepare forecast data if available
  const forecastDates = forecast?.map(point => point.timestamp) || [];
  const forecastValues = forecast?.map(point => point.value) || [];

  // Combine dates for confidence intervals
  const allDates = [...historicalDates, ...forecastDates];

  const traces = [
    // Historical data
    {
      name: 'Historical',
      x: historicalDates,
      y: historicalValues,
      type: 'scatter',
      mode: 'lines',
      line: {
        color: theme.palette.primary.main,
        width: 2,
      },
    },
    // Forecast
    ...(forecast ? [{
      name: 'Forecast',
      x: forecastDates,
      y: forecastValues,
      type: 'scatter',
      mode: 'lines+markers',
      line: {
        color: theme.palette.secondary.main,
        width: 2,
        dash: 'dash',
      },
      marker: {
        size: 6,
        color: theme.palette.secondary.main,
      },
    }] : []),
    // Confidence intervals
    ...(confidenceIntervals ? [{
      name: 'Confidence Interval',
      x: [...allDates, ...allDates.slice().reverse()],
      y: [...confidenceIntervals.upper, ...confidenceIntervals.lower.slice().reverse()],
      fill: 'toself',
      fillcolor: 'rgba(138, 43, 226, 0.1)',
      line: { color: 'transparent' },
      type: 'scatter',
      showlegend: false,
    }] : []),
  ];

  const layout = {
    title: {
      text: title,
      font: {
        color: theme.palette.text.primary,
      },
    },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
    height: height,
    margin: { t: 50, r: 20, l: 40, b: 40 },
    xaxis: {
      showgrid: true,
      gridcolor: 'rgba(255,255,255,0.1)',
      tickfont: { color: theme.palette.text.secondary },
    },
    yaxis: {
      showgrid: true,
      gridcolor: 'rgba(255,255,255,0.1)',
      tickfont: { color: theme.palette.text.secondary },
    },
    legend: {
      font: { color: theme.palette.text.primary },
    },
    hovermode: 'x unified',
  };

  const config = {
    responsive: true,
    displayModeBar: false,
  };

  return (
    <Box sx={{
      background: 'rgba(26, 26, 46, 0.8)',
      backdropFilter: 'blur(10px)',
      borderRadius: 2,
      border: '1px solid rgba(138, 43, 226, 0.2)',
      p: 2,
    }}>
      <Plot
        data={traces}
        layout={layout}
        config={config}
        style={{ width: '100%' }}
      />
    </Box>
  );
};

export default TimeSeriesChart; 